import { useMutation } from "@apollo/client";
import moment from "moment";
import { useState } from "react";
import type { GetProfileOneononeQueryQuery } from "types/graphql-schema";

import updateMeetingGroupMutation from "@apps/meeting-new/graphql/update-meeting-group-mutation";
import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";

import getProfileOneononeQuery from "../graphql/get-profile-oneonone-query";

type CandidateMeeting = NonNullable<
  NonNullable<
    NonNullable<GetProfileOneononeQueryQuery["candidateMeetings"]>["edges"][0]
  >["node"]
>;

const SelectOneononeMeetingDialog = ({
  candidateMeetings,
  profileFirstName,
  onClose,
}: {
  candidateMeetings: CandidateMeeting[];
  profileFirstName: string;
  onClose: () => void;
}) => {
  const label = useLabel();
  const [selectedMeetingGroupId, setSelectedMeetingGroupId] = useState<
    number | null
  >(null);
  const [updateMeetingGroup, { loading: saving }] = useMutation(
    updateMeetingGroupMutation,
    {
      refetchQueries: [getProfileOneononeQuery],
    },
  );

  const handleConfirm = async () => {
    if (!selectedMeetingGroupId) return;

    try {
      await updateMeetingGroup({
        variables: {
          meetingGroupId: selectedMeetingGroupId,
          isFormalOneonone: true,
        },
      });
      successNotificationVar({
        title: `Meeting confirmed as ${label("1-on-1")}`,
      });
      onClose();
    } catch {
      errorNotificationVar({
        title: `Failed to set meeting as ${label("1-on-1")}`,
      });
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="flex flex-col gap-6 border rounded-md p-6">
        <div>
          <ModalTitle onClose={onClose}>
            Select Meeting for {label("1-on-1", { titleCase: true })}
          </ModalTitle>
          <div className="text-sm text-gray-500 font-normal mr-16">
            We found some meetings with you and {profileFirstName}, select one
            to be your recurring {label("1-on-1")}.
          </div>
        </div>

        {/* Meeting list */}
        <div className="flex flex-col gap-3">
          {candidateMeetings.map((meeting) => (
            <label
              key={meeting.meetingGroupId}
              className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100"
            >
              <input
                type="radio"
                name="selectedMeeting"
                checked={selectedMeetingGroupId === meeting.meetingGroupId}
                onChange={() =>
                  setSelectedMeetingGroupId(meeting.meetingGroupId)
                }
                className="w-4 h-4 text-blue-600"
              />
              <span className="flex-1 text-sm text-gray-800">
                {meeting.title || "Untitled Meeting"}
              </span>
              {meeting.startDatetime && (
                <span className="text-sm text-gray-500">
                  Next: {moment(meeting.startDatetime).format("MMM D, h:mma")}
                </span>
              )}
            </label>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <Button theme={buttonTheme.default} onClick={onClose} text="Cancel" />
          <Button
            theme={buttonTheme.primary}
            onClick={handleConfirm}
            disabled={!selectedMeetingGroupId || saving}
            text={`Confirm Meeting as ${label("1-on-1", { titleCase: true })}`}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SelectOneononeMeetingDialog;
